import { List } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { updateMe as updateMeAction } from '../../../actions/user.actions';
import { SchoolStageSelected } from '../../../ampli';
import OnboardingStepSchulstufeImage from '../../../assets/onboarding/onboarding-step-schulstufe.svg';
import { spaces } from '../../../assets/styles/themes/tokens';
import { gradeAvatarMap } from '../../../constants';
import { selectUser } from '../../../reducers/user.selectors';
import type { OnboardingSteps } from '../../../routes/onboarding/OnboardingPage';
import services from '../../../services';
import useAction from '../../../utils/useAction';
import OnboardingOptionItem from '../OnboardingOptionItem';
import OnboardingStepContainer from '../OnboardingStepContainer';
import OnboardingStepImage from '../OnboardingStepImage';

interface OwnProps {
	setOnboardingStep: (step: keyof OnboardingSteps) => void;
	stepNum?: number;
	totalSteps?: number;
	onBackOverride?: (() => void) | null;
	onNextOverride?: () => void;
	hideImage?: boolean;
	compact?: boolean;
}

const OnboardingStepSchulstufe: React.FC<OwnProps> = ({
	setOnboardingStep,
	stepNum,
	totalSteps,
	onBackOverride,
	onNextOverride,
	hideImage,
	compact,
}) => {
	const { t } = useTranslation();
	const user = useSelector(selectUser);
	const updateMe = useAction(updateMeAction);
	const [schoolStages, setSchoolStages] = useState<Array<{ name: string }>>([]);
	const [selectedSchoolStages, setSelectedSchoolStages] = useState<string[]>(
		user?.schoolStages ?? []
	);

	useEffect(() => {
		if (user?.schoolStages) {
			setSelectedSchoolStages(user.schoolStages);
		}
	}, [user?.schoolStages]);

	const contentHeights = {
		default: 'calc(100vh - 527px)',
		smallScreen: 'calc(100vh - 329px)',
	};

	const handleClickOption = (stage: string) => {
		setSelectedSchoolStages((prevSelectedSchoolStages) => {
			const isSelected = prevSelectedSchoolStages.includes(stage);
			const updatedSchoolStages = isSelected
				? prevSelectedSchoolStages.filter((prevStage) => prevStage !== stage)
				: [...prevSelectedSchoolStages, stage];
			return updatedSchoolStages;
		});
	};

	const defaultOnBack = () => setOnboardingStep('bundesland');

	const sendSelectedStageEvent = () => {
		if (user) {
			services.track.ampliEventTrack({
				event: new SchoolStageSelected(),
				userProperties: {
					id: user.id,
					schoolStages: selectedSchoolStages,
				},
			});
		}
	};

	const onClickNext = async () => {
		await updateMe({ schoolStages: selectedSchoolStages });
		sendSelectedStageEvent();
		if (onNextOverride) {
			onNextOverride();
		} else {
			setOnboardingStep('schulfaecher');
		}
	};

	useEffect(() => {
		services.text.fetchSchoolStages().then((stages) => {
			setSchoolStages(stages);
		});
	}, []);

	return (
		<>
			<OnboardingStepContainer
				title={t('What grade level do you teach?')}
				subtitle={t('This information will help us make appropriate recommendations for you.')}
				stepNum={stepNum ?? 4}
				totalSteps={totalSteps}
				onNext={onClickNext}
				onBack={onBackOverride === undefined ? defaultOnBack : (onBackOverride ?? undefined)}
				contentHeights={contentHeights}
				compact={compact}
			>
				<List
					dense
					sx={{
						width: '100%',
						paddingRight: spaces.xxs,
						display: 'flex',
						flexDirection: 'column',
						gap: spaces.md,
					}}
				>
					{schoolStages.map((stage) => (
						<OnboardingOptionItem
							key={stage.name}
							label={stage.name}
							avatar={gradeAvatarMap[stage.name as keyof typeof gradeAvatarMap]}
							isSelected={selectedSchoolStages.includes(stage.name)}
							isRadioButton={false}
							onClick={() => handleClickOption(stage.name)}
						/>
					))}
				</List>
			</OnboardingStepContainer>
			{!hideImage && <OnboardingStepImage stepImage={<OnboardingStepSchulstufeImage />} />}
		</>
	);
};

export default OnboardingStepSchulstufe;
